import * as React from "react";
import { Package, Plus, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { Modal } from "@/components/Modal.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { useToast } from "@/components/Toast.js";


interface BoxItem { id: string; boxId: string; itemId: string; itemName: string | null; createdAt: string }
interface Box { id: string; label: string; number: number; season: string | null; spaceId: string | null; spaceName: string | null; items: BoxItem[]; createdAt: string }

const SEASONS = [
  { key: null, label: "All", emoji: "📦" },
  { key: "moving", label: "Moving", emoji: "🚚" },
  { key: "holiday", label: "Holiday", emoji: "🎄" },
  { key: "winter", label: "Winter", emoji: "❄️" },
  { key: "spring", label: "Spring", emoji: "🌸" },
  { key: "summer", label: "Summer", emoji: "☀️" },
  { key: "fall", label: "Fall", emoji: "🍂" },
  { key: "guest", label: "Guest", emoji: "🛏️" },
] as const;

export default function BoxesPage() {
  const toast = useToast();
  const [boxes, setBoxes] = React.useState<Box[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [season, setSeason] = React.useState<string | null>(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const [newLabel, setNewLabel] = React.useState("");
  const [newNumber, setNewNumber] = React.useState(1);
  const [newSeason, setNewSeason] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<Box | null>(null);
  const [expandedBox, setExpandedBox] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<{ id: string; name: string }[]>([]);
  const [addItemBoxId, setAddItemBoxId] = React.useState<string | null>(null);
  const [searchItem, setSearchItem] = React.useState("");

  const fetchBoxes = React.useCallback(async () => {
    try {
      const url = season ? `/api/boxes?season=${season}` : "/api/boxes";
      setBoxes(await apiFetch<Box[]>(url));
    } catch { /* ignore */ }
    setLoading(false);
  }, [season]);

  React.useEffect(() => { fetchBoxes(); }, [fetchBoxes]);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    try {
      await apiFetch("/api/boxes", { method: "POST", body: JSON.stringify({ label: newLabel, number: newNumber, season: newSeason }) });
      toast.show("Box created!");
      setShowAdd(false); setNewLabel(""); setNewNumber((boxes.length || 0) + 1);
      fetchBoxes();
    } catch (e: any) { toast.show(e.message ?? "Failed", "error"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/api/boxes/${deleteTarget.id}`, { method: "DELETE" });
      toast.show("Box deleted");
      setDeleteTarget(null); fetchBoxes();
    } catch (e: any) { toast.show(e.message ?? "Failed", "error"); }
  };

  const openAddItem = async (boxId: string) => {
    setAddItemBoxId(boxId);
    setSearchItem("");
    setItems([]);
  };

  const searchItems = React.useCallback(async (q: string) => {
    if (!q.trim()) { setItems([]); return; }
    try { setItems(await apiFetch<{ id: string; name: string }[]>(`/api/items?search=${encodeURIComponent(q)}&limit=20`)); } catch { /* ignore */ }
  }, []);

  React.useEffect(() => {
    if (!addItemBoxId) return;
    const t = setTimeout(() => searchItems(searchItem), 300);
    return () => clearTimeout(t);
  }, [searchItem, addItemBoxId, searchItems]);

  const addItemToBox = async (itemId: string) => {
    if (!addItemBoxId) return;
    try {
      await apiFetch(`/api/boxes/${addItemBoxId}/items`, { method: "POST", body: JSON.stringify({ itemId }) });
      toast.show("Item added to box");
      setAddItemBoxId(null); setSearchItem(""); fetchBoxes();
    } catch (e: any) { toast.show(e.message ?? "Failed", "error"); }
  };

  const removeItemFromBox = async (boxId: string, itemId: string) => {
    try {
      await apiFetch(`/api/boxes/${boxId}/items/${itemId}`, { method: "DELETE" });
      fetchBoxes();
    } catch { /* ignore */ }
  };

  const fi = "w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink flex items-center gap-2 font-headline tracking-[-0.01em]"><Package className="w-6 h-6 text-primary" />Boxes &amp; Seasons</h1>
        <button onClick={() => { setNewNumber((boxes.length || 0) + 1); setShowAdd(true); }} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-1 hover:bg-amethyst-ink transition-colors"><Plus className="w-4 h-4" />New Box</button>
      </div>

      {/* Season filter */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {SEASONS.map((s) => (
          <button key={s.label} onClick={() => setSeason(s.key)} className={cn("px-3 py-1.5 rounded-pill text-[0.8125rem] font-medium whitespace-nowrap transition-colors", season === s.key ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-amethyst-soft")}>
            {s.emoji} {s.label}
          </button>
        ))}
      </div>

      {/* Add box form */}
      {showAdd && (
        <div className="bg-white rounded-lg border border-clay-border p-4 space-y-3">
          <input placeholder="Box label (e.g. Kitchen Stuff)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className={fi} />
          <div className="flex gap-2 items-center">
            <label className="text-[0.8125rem] text-faint shrink-0">Box #</label>
            <input type="number" min={1} value={newNumber} onChange={(e) => setNewNumber(Number(e.target.value))} className={fi + " w-20 tabular-nums"} />
          </div>
          <div className="flex gap-1 flex-wrap">
            {SEASONS.slice(1).map((s) => (
              <button key={s.key} onClick={() => setNewSeason(newSeason === s.key ? null : s.key)} className={cn("px-3 py-1 rounded-pill text-[0.8125rem] font-medium transition-colors", newSeason === s.key ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-amethyst-soft")}>
                {s.emoji} {s.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium">Cancel</button>
            <button onClick={handleCreate} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Create</button>
          </div>
        </div>
      )}

      {/* Box list */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-clay-muted rounded-lg animate-pulse" />)}</div>
      ) : boxes.length === 0 ? (
        <div className="bg-sage-wash rounded-lg border border-clay-border text-center py-16 px-6"><Package className="w-12 h-12 text-sage-deep mx-auto mb-3" /><p className="text-body">No boxes yet — create one to start packing things away.</p></div>
      ) : (
        <div className="space-y-3">
          {boxes.map((box) => (
            <div key={box.id} className="bg-white rounded-lg border border-clay-border p-4 hover:border-amethyst/40 transition-colors">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedBox(expandedBox === box.id ? null : box.id)}>
                <span className="text-2xl font-semibold text-primary tabular-nums">#{box.number}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">{box.label}</p>
                  <p className="text-[0.8125rem] text-faint"><span className="tabular-nums">{box.items.length}</span> item{box.items.length !== 1 ? "s" : ""}{box.season ? ` · ${box.season}` : ""}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); openAddItem(box.id); }} className="p-1.5 hover:bg-amethyst-soft rounded-md transition-colors" aria-label={`Add item to box #${box.number}`}><Plus className="w-4 h-4 text-primary" /></button>
                <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(box); }} className="p-1.5 hover:bg-error-wash rounded-md transition-colors" aria-label={`Delete box #${box.number}`}><Trash2 className="w-4 h-4 text-error" /></button>
              </div>
              {expandedBox === box.id && box.items.length > 0 && (
                <div className="mt-3 pt-3 border-t border-clay-border space-y-1">
                  {box.items.map((bi) => (
                    <div key={bi.id} className="flex items-center gap-2 text-sm py-1">
                      <span className="flex-1 text-body">{bi.itemName ?? `Item #${bi.itemId}`}</span>
                      <button onClick={() => removeItemFromBox(box.id, bi.itemId)} className="p-0.5 hover:bg-error-wash rounded-sm transition-colors" aria-label={`Remove ${bi.itemName ?? "item"} from box`}><X className="w-3.5 h-3.5 text-error" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add item to box modal */}
      <Modal open={!!addItemBoxId} onClose={() => { setAddItemBoxId(null); setSearchItem(""); }} title="Add Item to Box">
        <input placeholder="Search items..." value={searchItem} onChange={(e) => setSearchItem(e.target.value)} className={fi + " mb-3"} />
        <div className="max-h-60 overflow-y-auto space-y-1">
          {items.slice(0, 20).map((item) => (
            <button key={item.id} onClick={() => addItemToBox(item.id)} className="w-full text-left px-3 py-2 rounded-md hover:bg-amethyst-soft text-sm text-body transition-colors">{item.name}</button>
          ))}
          {searchItem && items.length === 0 && <p className="text-sm text-faint text-center py-4">No items found</p>}
          {!searchItem && <p className="text-sm text-faint text-center py-4">Type to search items</p>}
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="Delete Box" message={`Delete box #${deleteTarget?.number} "${deleteTarget?.label}"?`} confirmLabel="Delete" destructive onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}
